import { Link } from 'react-router-dom';
import { Bot, Trophy, Headphones, ShoppingBag, ArrowRight, Users, Zap, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const features = [
  {
    icon: Trophy,
    title: 'Matchmaking & Tournament Discovery',
    description: 'Help your users find and join the right tournaments. AI-powered recommendations based on skill level, game preferences, and availability.',
    color: '#00D4AA'
  },
  {
    icon: Headphones,
    title: 'AI Customer Support',
    description: 'Resolve 94% of support tickets automatically. 24/7 instant responses, smart escalation, and personalized assistance for every user.',
    color: '#6366F1'
  },
  {
    icon: ShoppingBag,
    title: 'Sales & Conversion',
    description: 'Intelligent upselling and cross-selling. The AI identifies opportunities and guides users to premium features and tournament entries.',
    color: '#F59E0B'
  }
];

const benefits = [
  'No data storage - fully stateless',
  'Works with any data source (API, Sheets, Excel)',
  'White-label with your branding',
  'Deploy on Telegram, WhatsApp, or web',
  '94% customer satisfaction rate',
  'Average 2.3s response time'
];

const stats = [
  { value: '94%', label: 'Tickets Resolved by AI' },
  { value: '2.3s', label: 'Avg Response Time' },
  { value: '+18%', label: 'Sales Conversion Boost' },
  { value: '24/7', label: 'Always Available' }
];

export function Landing() {
  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A] via-[#1E293B]/50 to-[#0F172A]" />

        {/* Nav */}
        <nav className="absolute top-0 left-0 right-0 z-20 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png" alt="XO" className="w-full h-full object-cover" />
              </div>
              <span className="font-bold text-xl text-white">XOClaw</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button className="bg-[#00D4AA] text-black hover:bg-[#00E6BB] font-bold">Sign In</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00D4AA]/10 border border-[#00D4AA]/30 mb-6">
              <Bot className="w-4 h-4 text-[#00D4AA]" />
              <span className="text-sm text-[#00D4AA] font-medium">AI Agent for Esports Platforms</span>
            </div>
            
            <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Matchmaking, Sales & Support
              <span className="block text-[#00D4AA]">All in One AI Agent</span>
            </h1>
            
            <p className="text-lg text-[#94A3B8] mb-8 max-w-2xl">
              XOClaw is a white-label AI agent that helps esports platforms engage users, 
              boost sales conversions, and resolve support tickets automatically.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a href="/demo/chat" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#00D4AA] text-black hover:bg-[#00E6BB] font-bold text-base px-8" data-testid="demo-chat-btn">
                  See Demo Chat
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-[#475569]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#00D4AA] mb-2">{stat.value}</div>
                <div className="text-sm text-[#94A3B8]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What XOClaw Does for Your Business */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
              What XOClaw Does for Your Business
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Three core AI functions that transform how your platform engages with users
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="p-6 rounded-lg bg-[#1E293B] border border-[#475569] hover:border-[#00D4AA]/50 transition-colors"
              >
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${feature.color}15`, border: `1px solid ${feature.color}30` }}
                >
                  <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                </div>
                <h3 className="font-bold text-xl text-white mb-2">{feature.title}</h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why XOClaw */}
      <section className="py-24 bg-[#1E293B]/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-bold text-2xl sm:text-3xl text-white mb-6">
                Why Choose XOClaw?
              </h2>
              <div className="grid gap-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00D4AA] flex-shrink-0" />
                    <span className="text-[#94A3B8]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg bg-[#0F172A] border border-[#475569] p-6">
              <div className="text-center">
                <Users className="w-16 h-16 mx-auto text-[#6366F1] mb-4" />
                <h3 className="font-bold text-xl text-white mb-2">White-Label Ready</h3>
                <p className="text-[#94A3B8] text-sm mb-4">
                  XOClaw becomes your branded AI agent. Arena Esports has ArenaClaw — 
                  your brand, your agent, fully customized.
                </p>
                <div className="flex justify-center gap-2">
                  <span className="px-3 py-1 rounded-full bg-[#00D4AA]/10 text-[#00D4AA] text-xs">Your Logo</span>
                  <span className="px-3 py-1 rounded-full bg-[#6366F1]/10 text-[#6366F1] text-xs">Your Colors</span>
                  <span className="px-3 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-xs">Your Domain</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/30 mb-6">
            <Zap className="w-4 h-4 text-[#6366F1]" />
            <span className="text-sm text-[#6366F1] font-medium">Get Started Today</span>
          </div>
          <h2 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
            Ready to Deploy Your AI Agent?
          </h2>
          <p className="text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            Contact XO to get your own branded AI agent deployed on your platform.
          </p>
          <Link to="/login">
            <Button size="lg" className="bg-[#00D4AA] text-black hover:bg-[#00E6BB] font-bold text-base px-8">
              Sign In
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#475569]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_tournament-finder-2/artifacts/bgo2ljk1_XO%20Logo%282200x2200%29.png" alt="XO" className="w-full h-full object-cover" />
              </div>
              <span className="font-bold text-white">XOClaw</span>
            </div>
            <p className="text-sm text-[#94A3B8]">
              AI Agent Platform by XO
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
